import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { RefreshCw, AlertTriangle, Clock } from './icons';
import {
  useNetworkStatus,
  useSyncStatusQuery,
  useSyncMutation,
  useRetryFailedMutation
} from '../hooks/queries/useOfflineRequestsQuery';

interface HeaderSyncIndicatorProps {
  style?: any;
}

export const HeaderSyncIndicator: React.FC<HeaderSyncIndicatorProps> = ({ style }) => {
  const isOnline = useNetworkStatus();
  const { data: syncStatus } = useSyncStatusQuery();
  const syncMutation = useSyncMutation();
  const retryFailedMutation = useRetryFailedMutation();

  // Calculate total pending items (pending + failed)
  const totalPending = (syncStatus?.pendingCount || 0) + (syncStatus?.failedCount || 0);

  // Don't render if offline - user will see the "Offline" badge instead
  if (!isOnline) {
    return null;
  }

  // Don't render if there are no pending items and not currently syncing
  if (totalPending === 0 && !syncStatus?.isSyncing && !syncMutation.isPending) {
    return null;
  }

  const handleSync = async () => {
    if (!isOnline) {
      return;
    }

    if (syncMutation.isPending || retryFailedMutation.isPending) {
      return;
    }

    // Determine whether to retry failed items or do regular sync
    if (syncStatus?.failedCount > 0) {
      try {
        retryFailedMutation.mutate();
      } catch (error) {
        console.error('HeaderSyncIndicator: Error in retryFailed:', error);
      }
    } else {
      try {
        syncMutation.mutate();
      } catch (error) {
        console.error('HeaderSyncIndicator: Error in sync:', error);
      }
    }
  };

  const getIndicatorColor = () => {
    if (!isOnline) return '#dc3545'; // Red for offline
    if (syncStatus?.failedCount) return '#dc3545'; // Red for failed
    if (syncStatus?.pendingCount) return '#ffc107'; // Yellow for pending
    return '#007bff'; // Blue when syncing
  };

  const getIndicatorIcon = () => {
    const iconSize = Platform.OS !== 'web' ? 14 : 16; // Only change mobile
    const iconProps = { size: iconSize, color: '#fff' };

    if (syncStatus?.isSyncing || syncMutation.isPending || retryFailedMutation.isPending) {
      return <ActivityIndicator size="small" color="#fff" />;
    }
    if (syncStatus?.failedCount) return <AlertTriangle {...iconProps} />;
    if (syncStatus?.pendingCount) return <Clock {...iconProps} />;
    return <RefreshCw {...iconProps} />;
  };

  const getButtonText = () => {
    if (syncStatus?.isSyncing || syncMutation.isPending) return 'Syncing';
    if (retryFailedMutation.isPending) return 'Retrying';
    if (syncStatus?.failedCount) return 'Retry';
    return 'Sync';
  };

  return (
    <TouchableOpacity
      style={[
        styles.syncButton,
        { backgroundColor: getIndicatorColor() },
        (!isOnline || syncStatus?.isSyncing || syncMutation.isPending || retryFailedMutation.isPending) && styles.syncButtonDisabled,
        style
      ]}
      onPress={handleSync}
      disabled={!isOnline || syncStatus?.isSyncing || syncMutation.isPending || retryFailedMutation.isPending}
      testID="header-sync-indicator"
    >
      <View style={styles.iconContainer}>
        {getIndicatorIcon()}
      </View>
      <View style={styles.textContainer}>
        <Text style={styles.buttonText}>{getButtonText()}</Text>
        <Text style={styles.countText}>{totalPending}</Text>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  syncButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Platform.OS !== 'web' ? 10 : 12, // Only change mobile
    paddingVertical: Platform.OS !== 'web' ? 5 : 6,     // Only change mobile
    borderRadius: Platform.OS !== 'web' ? 14 : 16,      // Only change mobile
    gap: Platform.OS !== 'web' ? 6 : 8,                 // Only change mobile
    minHeight: Platform.OS !== 'web' ? 28 : 32,         // Only change mobile
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  syncButtonDisabled: {
    opacity: 0.7,
  },
  iconContainer: {
    width: Platform.OS !== 'web' ? 14 : 16, // Only change mobile
    height: Platform.OS !== 'web' ? 14 : 16, // Only change mobile
    justifyContent: 'center',
    alignItems: 'center',
  },
  textContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Platform.OS !== 'web' ? 4 : 6, // Only change mobile
  },
  buttonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '500',
  },
  countText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
    paddingHorizontal: 5,
    paddingVertical: 1,
    minWidth: 18,
    textAlign: 'center',
  },
});